import { Link, useLocation } from 'react-router';

import { TabNav as RadixTabsNav } from '@radix-ui/themes';
import classNames from 'classnames';

import { renderIcon } from '../utils';
import styles from './TabsNav.module.less';
import {
  TabsNavProps,
  sizeToRadixTabsSizeMap,
  sizeToTabMap,
} from './TabsNav.types';

function TabNav({
  to,
  href,
  active,
  children,
}: {
  to?: string;
  href?: string;
  active?: boolean;
  children: React.ReactNode;
}) {
  if (to) {
    return (
      <RadixTabsNav.Link key={to} asChild active={active}>
        <Link to={to}>{children}</Link>
      </RadixTabsNav.Link>
    );
  }

  return (
    <RadixTabsNav.Link href={href} key={href} active={active}>
      {children}
    </RadixTabsNav.Link>
  );
}

export const TabsNav = ({
  tabs,
  size = '2',

  className,
  ...props
}: TabsNavProps) => {
  const location = useLocation();

  return (
    <RadixTabsNav.Root
      {...props}
      className={classNames(className, styles.tabs, styles[`tabsSize${size}`])}
      size={sizeToRadixTabsSizeMap[size]}
    >
      {tabs.map(
        ({
          label,
          dataTestId,
          to,
          href,
          active,
          disabled,
          iconStart,
          iconEnd,
          iconStartSize = 16,
          iconEndSize = 16,
        }) => (
          <TabNav
            key={to || href}
            to={to}
            href={href}
            active={
              typeof active === 'function'
                ? active(location.pathname)
                : location.pathname === to
            }
          >
            <div
              data-testid={
                dataTestId ?? `tab-${label?.toString()?.toLowerCase()}`
              }
              className='flex items-center gap-fly-3'
            >
              {iconStart && renderIcon(iconStart, iconStartSize)}
              <span className={sizeToTabMap[size]}>{label}</span>
              {iconEnd && renderIcon(iconEnd, iconEndSize)}
            </div>
          </TabNav>
        )
      )}
    </RadixTabsNav.Root>
  );
};
